import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Form, Input, Select, Space, message } from 'antd';
import './styles.css';

const { Option } = Select;

const TritonConverter = () => {
  const [form] = Form.useForm();
  const [collectionName, setCollectionName] = useState('');
  const [currentLanguage, setCurrentLanguage] = useState('');
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [result, setResult] = useState({});
  const [langCache, setLangCache] = useState({});
  const [args, setArgs] = useState([]);
  const [loading, setLoading] = useState(false);

  const lineWithValuePattern = /["'].*?["']|["'].*?|.*?["']/i;

  const handleInput = () => {
    if (!collectionName) {
      setOutputText('Please enter a Collection Name');
      return;
    }
    if (!currentLanguage) {
      setOutputText('Waiting for select language');
      return;
    }

    const lines = inputText.split('\n');
    const argsRegex = new RegExp(args.join('|'));
    let output = '';
    const newLangCache = { ...langCache };
    const newResult = { ...result };
    let counter = 0;

    lines.forEach(line => {
      newLangCache[currentLanguage] = (newLangCache[currentLanguage] || '') + line + '\n';
      
      if (!line.match(lineWithValuePattern)) {
        output += line + '\n';
        return;
      }

      const data = line.includes(':') ? line.split(/:(.*)/s) : line.split(/-(.*)/s);
      const key = line.includes(':') ? data[0] + ': ' : data[0] + '- ';
      const value = data[1];

      if (!args.length || !value.match(argsRegex)) {
        const locId = `${collectionName}_${counter}`;
        output += key + `'[lang]${locId}[/lang]'\n`;
        if (!newResult[locId]) newResult[locId] = {};
        newResult[locId][currentLanguage] = parseResulting(value);
        counter++;
        return;
      }

      const re = /[%<\[<\{]{1}[a-zA-Z_-]+[%>\]\}]{1}/g;
      const locId = `${collectionName}_${counter}`;
      output += key + `'[lang]${locId}[args]`;
      let newVal = value;
      let argNum = 1;
      let m;

      do {
        m = re.exec(value);
        if (m && args.includes(m[0])) {
          output += `[arg]${m[0]}[/arg]`;
          newVal = newVal.replace(m[0], `%${argNum}`);
          argNum++;
        }
      } while (m);

      if (!newResult[locId]) newResult[locId] = {};
      newResult[locId][currentLanguage] = parseResulting(newVal);
      output += '[/args][/lang]\'\n';
      counter++;
    });

    setOutputText(output);
    setLangCache(newLangCache);
    setResult(newResult);
  };

  const parseResulting = (value) => {
    value = value.trim();
    const match = value.match(/^['"].*?['"]$/i);
    if (match) {
      return match[0].substring(1, match[0].length - 1);
    }
    return value;
  };

  const handleUpload = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const data = {
        database: {
          host: values.dbHost,
          user: values.dbUser,
          pass: values.dbPass,
          base: values.dbBase,
          pref: values.dbPref,
          port: parseInt(values.dbPort)
        },
        collection: collectionName,
        data: result
      };

      const response = await fetch('/api/triton/upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();
      if (result.success) {
        message.success('Successfully saved to database');
      } else {
        message.error(`Error saving to database: ${result.message}`);
      }
    } catch (error) {
      message.error('Error saving to database');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setLangCache({});
    setResult({});
    setCurrentLanguage('');
    setCollectionName('');
    setInputText('');
    setOutputText('Please enter a Collection Name');
    form.resetFields();
  };

  useEffect(() => {
    handleInput();
  }, [inputText, collectionName, currentLanguage, args]);

  return (
    <div className="triton-converter">
      <div className="converter-header">
        <Space direction="vertical" style={{ width: '100%' }}>
          <Form form={form} layout="vertical">
            <Form.Item
              label="Collection Name"
              name="collectionName"
              rules={[{ required: true, message: 'Please enter collection name' }]}
            >
              <Input
                value={collectionName}
                onChange={(e) => setCollectionName(e.target.value)}
                placeholder="Enter collection name"
              />
            </Form.Item>

            <Form.Item
              label="Language"
              name="language"
              rules={[{ required: true, message: 'Please select language' }]}
            >
              <Select
                value={currentLanguage}
                onChange={setCurrentLanguage}
                placeholder="Choose language"
              >
                <Option value="ru_RU">Russia</Option>
                <Option value="en_US">United States of America (USA)</Option>
                {/* Add more language options here */}
              </Select>
            </Form.Item>

            <Form.Item label="Database Configuration">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Form.Item name="dbHost" rules={[{ required: true }]}>
                  <Input placeholder="Database host" />
                </Form.Item>
                <Form.Item name="dbPort" rules={[{ required: true }]}>
                  <Input placeholder="Database port" defaultValue="3306" />
                </Form.Item>
                <Form.Item name="dbUser" rules={[{ required: true }]}>
                  <Input placeholder="Database user" />
                </Form.Item>
                <Form.Item name="dbPass" rules={[{ required: true }]}>
                  <Input.Password placeholder="Database password" />
                </Form.Item>
                <Form.Item name="dbBase" rules={[{ required: true }]}>
                  <Input placeholder="Database base" />
                </Form.Item>
                <Form.Item name="dbPref" rules={[{ required: true }]}>
                  <Input placeholder="Triton table prefix" defaultValue="triton_" />
                </Form.Item>
              </Space>
            </Form.Item>
          </Form>
        </Space>
      </div>

      <div className="converter-content">
        <div className="input-section">
          <h3>Input</h3>
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Enter translations..."
            rows={10}
          />
        </div>
        <div className="output-section">
          <h3>Output</h3>
          <textarea
            value={outputText}
            readOnly
            rows={10}
          />
        </div>
      </div>

      <div className="converter-footer">
        <Space>
          <Button type="primary" onClick={handleUpload} loading={loading}>
            Upload translations to database
          </Button>
          <Button onClick={handleReset}>New Translation Doc</Button>
        </Space>
      </div>
    </div>
  );
};

export default TritonConverter; 